<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ItemsByStageCard from './ItemsByStageCard.vue';
import TimePerStageCard from './TimePerStageCard.vue';
import RecentActivityCard from './RecentActivityCard.vue';
import ForgottenLeadsCard from './ForgottenLeadsCard.vue';

const props = defineProps({
  metrics: {
    type: Object,
    required: true,
  },
  recentActivity: {
    type: Array,
    required: true,
  },
  currentUserName: {
    type: String,
    default: '',
  },
  hasNoData: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['view-forgotten-leads']);

const conversionRatePercentage = computed(() => {
  const total = props.metrics.totalItems || 0;
  const won = props.metrics.wonItems || 0;
  return total > 0 ? Math.round((won / total) * 100) : 0;
});

const lossRatePercentage = computed(() => {
  const total = props.metrics.totalItems || 0;
  const lost = props.metrics.lostItems || 0;
  return total > 0 ? Math.round((lost / total) * 100) : 0;
});

const formattedTotalValue = computed(() => {
  const value = props.metrics.totalValue || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
});

const formattedAvgValue = computed(() => {
  const value = props.metrics.avgValue || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
});

const getMetricIcon = type => {
  const icons = {
    total: 'i-lucide-briefcase',
    active: 'i-lucide-play-circle',
    won: 'i-lucide-check-circle',
    lost: 'i-lucide-x-circle',
  };
  return icons[type] || 'i-lucide-info';
};

const getMetricColor = type => {
  const colors = {
    total: 'text-n-blue-11',
    active: 'text-n-violet-11',
    won: 'text-n-teal-11',
    lost: 'text-n-ruby-11',
  };
  return colors[type] || 'text-n-slate-11';
};

const getMetricBg = type => {
  const colors = {
    total: 'bg-n-blue-3',
    active: 'bg-n-violet-3',
    won: 'bg-n-teal-3',
    lost: 'bg-n-ruby-3',
  };
  return colors[type] || 'bg-n-alpha-2';
};
</script>

<template>
  <div class="space-y-6 max-w-[1400px] mx-auto">
    <!-- Empty State -->
    <div
      v-if="hasNoData"
      class="flex flex-col items-center justify-center gap-4 py-12"
    >
      <Icon icon="i-lucide-box" class="size-16 text-n-slate-9" />
      <div class="text-center">
        <p class="text-base font-medium text-n-slate-12 mb-2">
          {{ $t('PIPELINE.UNIFIED_DASHBOARD.EMPTY_PIPELINE.TITLE') }}
        </p>
        <p class="text-sm text-n-slate-11 max-w-md">
          {{ $t('PIPELINE.UNIFIED_DASHBOARD.EMPTY_PIPELINE.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <!-- Key Metrics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <!-- Total Items -->
        <div class="p-5 rounded-lg border border-n-weak bg-n-solid-2">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg"
              :class="[getMetricBg('total')]"
            >
              <Icon
                :icon="getMetricIcon('total')"
                :class="getMetricColor('total')"
              />
            </div>
          </div>
          <div>
            <p class="text-3xl font-semibold text-n-slate-12 mb-1">
              {{ metrics.totalItems }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('PIPELINE.DASHBOARD.METRICS.TOTAL_ITEMS') }}
            </p>
          </div>
        </div>

        <!-- Active Items -->
        <div class="p-5 rounded-lg border border-n-weak bg-n-solid-2">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg"
              :class="[getMetricBg('active')]"
            >
              <Icon
                :icon="getMetricIcon('active')"
                :class="getMetricColor('active')"
              />
            </div>
          </div>
          <div>
            <p class="text-3xl font-semibold text-n-slate-12 mb-1">
              {{ metrics.activeItems }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('PIPELINE.DASHBOARD.METRICS.ACTIVE_ITEMS') }}
            </p>
          </div>
        </div>

        <!-- Won Items -->
        <div class="p-5 rounded-lg border border-n-weak bg-n-solid-2">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg"
              :class="[getMetricBg('won')]"
            >
              <Icon
                :icon="getMetricIcon('won')"
                :class="getMetricColor('won')"
              />
            </div>
            <span
              class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-n-teal-3 text-n-teal-11 border border-n-teal-6"
            >
              {{ conversionRatePercentage }}%
            </span>
          </div>
          <div>
            <p class="text-3xl font-semibold text-n-slate-12 mb-1">
              {{ metrics.wonItems }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('PIPELINE.DASHBOARD.METRICS.WON_ITEMS') }}
            </p>
          </div>
        </div>

        <!-- Lost Items -->
        <div class="p-5 rounded-lg border border-n-weak bg-n-solid-2">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg"
              :class="[getMetricBg('lost')]"
            >
              <Icon
                :icon="getMetricIcon('lost')"
                :class="getMetricColor('lost')"
              />
            </div>
            <span
              class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6"
            >
              {{ lossRatePercentage }}%
            </span>
          </div>
          <div>
            <p class="text-3xl font-semibold text-n-slate-12 mb-1">
              {{ metrics.lostItems }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('PIPELINE.DASHBOARD.METRICS.LOST_ITEMS') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Value Metrics (only shown when at least one card has a value) -->
      <div
        v-if="metrics.cardsWithValueCount > 0"
        class="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <!-- Total Value -->
        <div class="p-5 rounded-lg border border-n-teal-6 bg-n-teal-3">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-teal-5"
            >
              <Icon icon="i-lucide-banknote" class="text-n-teal-11" />
            </div>
            <span
              class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-n-teal-5 text-n-teal-11 border border-n-teal-7"
            >
              {{ metrics.cardsWithValueCount }}
              {{ $t('PIPELINE.CARDS') }}
            </span>
          </div>
          <div>
            <p class="text-2xl font-semibold text-n-teal-11 mb-1">
              {{ formattedTotalValue }}
            </p>
            <p class="text-xs text-n-teal-11/70">
              {{ $t('PIPELINE.DASHBOARD.METRICS.TOTAL_VALUE') }}
            </p>
          </div>
        </div>

        <!-- Average Value -->
        <div class="p-5 rounded-lg border border-n-amber-6 bg-n-amber-3">
          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-amber-5"
            >
              <Icon icon="i-lucide-calculator" class="text-n-amber-11" />
            </div>
          </div>
          <div>
            <p class="text-2xl font-semibold text-n-amber-11 mb-1">
              {{ formattedAvgValue }}
            </p>
            <p class="text-xs text-n-amber-11/70">
              {{ $t('PIPELINE.DASHBOARD.METRICS.AVG_VALUE') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <!-- Items by Stage -->
        <ItemsByStageCard :items-by-stage="metrics.itemsByStage" />

        <!-- Average Time per Stage -->
        <TimePerStageCard :avg-time-per-stage="metrics.avgTimePerStage" />
      </div>

      <!-- Fase 2.4 — Leads esquecidos (SLA excedido por etapa) -->
      <ForgottenLeadsCard
        :sla-exceeded-total="metrics.slaExceededTotal || 0"
        :sla-exceeded-by-stage="metrics.slaExceededByStage || {}"
        @view-kanban="emit('view-forgotten-leads')"
      />

      <!-- Recent Activity -->
      <RecentActivityCard
        :activities="recentActivity"
        :current-user-name="currentUserName"
      />
    </template>
  </div>
</template>
